import React from 'react';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../theme';

/**
 * A reusable ProgressBar component
 * @param {object} props
 * @param {number} props.value - The progress value (0-100)
 * @param {string} props.height - Height of the progress bar (default: "8px")
 * @param {string} props.bgColor - Background color of the progress track (optional)
 * @param {string} props.progressColor - Color of the progress indicator (optional)
 * @param {string} props.className - Additional CSS classes
 * @param {React.CSSProperties} props.sx - Additional styles
 * @param {string} props.borderRadius - Border radius (default: "full")
 */
const ProgressBar = React.forwardRef(({
  value = 0,
  height = "8px",
  bgColor,
  progressColor,
  className = "",
  sx = {},
  borderRadius = "full",
  ...props
}, ref) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Ensure value is between 0 and 100
  const progressValue = Math.min(100, Math.max(0, value));
  
  // Default colors based on theme
  const defaultBgColor = theme.palette.mode === "dark" 
    ? colors.grey[800] 
    : colors.grey[200];
    
  const defaultProgressColor = colors.primary[400];

  return (
    <Box
      ref={ref}
      className={`progress-bar ${className}`}
      sx={{
        position: 'relative',
        height,
        width: '100%',
        overflow: 'hidden',
        borderRadius: borderRadius === "full" ? "999px" : borderRadius,
        backgroundColor: bgColor || defaultBgColor,
        ...sx
      }}
      {...props}
    >
      <Box
        className="progress-indicator"
        sx={{
          height: '100%',
          width: '100%',
          backgroundColor: progressColor || defaultProgressColor,
          transition: 'transform 0.3s ease-in-out',
          transform: `translateX(-${100 - progressValue}%)`,
        }}
      />
    </Box>
  );
});

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
